import { skipToken, useQuery } from "@tanstack/react-query";

import { getInvitations, getMembers } from "./api/organization-api";
import type { Invitation, Member } from "./types";

interface UseOrgDetailReturn {
  members: Member[];
  invitations: Invitation[];
  isLoading: boolean;
  error: string | null;
}

export const orgDetailKey = (orgUnitId: string | undefined): readonly unknown[] =>
  ["organization", "detail", orgUnitId] as const;

/**
 * The members and invitations behind the org unit detail panel.
 *
 * One query over both lists rather than two, because the panel takes a single
 * loading flag and a single error slot: two queries would be recombined into
 * that same pair of booleans at the call site, and the recombination is where a
 * half-painted panel and a rule for which error wins would have to be invented.
 * The panel has never been able to show one list without the other, and a
 * mechanical migration is not the place to change what it can display.
 */
export function useOrgDetail(orgUnitId: string | undefined): UseOrgDetailReturn {
  const query = useQuery({
    queryKey: orgDetailKey(orgUnitId),
    queryFn:
      orgUnitId === undefined
        ? skipToken
        : async (): Promise<{ members: Member[]; invitations: Invitation[] }> => {
            const [members, invitations] = await Promise.all([
              getMembers(orgUnitId),
              getInvitations(orgUnitId),
            ]);
            return { members, invitations };
          },
  });

  const hasUnit = orgUnitId !== undefined;

  return {
    members: query.data?.members ?? [],
    invitations: query.data?.invitations ?? [],
    isLoading: hasUnit && query.isPending,
    error:
      hasUnit && query.isError && query.data === undefined ? "Failed to load member details" : null,
  };
}
